import { 
  Box, 
  Button, 
  Container, 
  Flex, 
  Heading, 
  Text, 
  VStack, 
  HStack, 
  Image, 
  Icon,
  IconButton,
  Spinner,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  Grid,
  FormHelperText,
  useToast,
  Tooltip
} from '@chakra-ui/react';
import { keyframes } from "@emotion/react";
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { FaThumbtack, FaPlus, FaCheck, FaExpand, FaCompress } from 'react-icons/fa';
import React, { useEffect, useState, lazy, Suspense } from 'react';
import AppHeader from '../components/AppHeader';
import { supabase, getCachedAuthUser } from '../lib/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import AppButton, { AppItemProps } from '../components/AppButton';
import AddPinButton from '../components/AddPinButton';
import { importAppIcon } from '../utils/imageHelpers';

// Subtle glow animation
const pulseGlow = keyframes`
  0% { box-shadow: 0 0 5px 0 rgba(255, 255, 255, 0.3); }
  50% { box-shadow: 0 0 20px 0 rgba(255, 255, 255, 0.4); }
  100% { box-shadow: 0 0 5px 0 rgba(255, 255, 255, 0.3); }
`;

// Category row component
const CategoryRow = ({
  title,
  apps,
  color = "blue",
  onPinToggle,
  isPinMode = false,
  isFullscreen = false
}: {
  title: string;
  apps: Array<{name: string; icon: string; url: string; isPinned?: boolean}>;
  color?: string;
  onPinToggle?: (app: {name: string; icon: string; url: string}) => void;
  isPinMode?: boolean;
  isFullscreen?: boolean;
}) => {
  return (
    <Box mb={8}>
      <Heading 
        size="md" 
        mb={4}
        pl={4}
        color="white"
        fontWeight="semibold"
        letterSpacing="wider"
        textAlign="left"
      >
        {title}
      </Heading>
      
      <Box 
        position="relative"
        overflowX="auto"
        p={1}
        css={{
          '&::-webkit-scrollbar': {
            display: 'none'
          },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none'
        }}
      >
        <HStack spacing={4} px={4} py={2}>
          {apps.map((app, index) => (
            <Box key={app.name + index} flex="0 0 auto">
              <AppButton 
                item={{
                  id: app.name,
                  name: app.name,
                  url: app.url,
                  color: color,
                  isPinned: app.isPinned,
                  isPinMode: isPinMode,
                  isFullscreen: isFullscreen
                }}
                type="theatre"
                iconName={app.icon}
                onPinToggle={onPinToggle ? (item) => onPinToggle({
                  name: item.name,
                  icon: app.icon,
                  url: item.url
                }) : undefined}
              />
            </Box>
          ))}
        </HStack>
      </Box>
    </Box>
  );
};

const TheatreApps = () => {
  // Simplified dark theme colors
  const bgGradient = "linear-gradient(180deg, #0F0F0F 0%, #121212 50%, #080808 100%)";
  const textColor = "white";
  const subtitleColor = "gray.300";
  
  // Pin mode state
  const [isPinMode, setIsPinMode] = React.useState(false);
  // Fullscreen mode state
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  
  // Pinned apps state
  const [pinnedApps, setPinnedApps] = React.useState<Array<{name: string; icon: string; url: string}>>([]);
  const [isLoadingPinnedApps, setIsLoadingPinnedApps] = React.useState(true);

  // Auth state from AuthContext
  const { user, userProfile, isSubscriber } = useAuth();

  // Add subscription modal disclosure
  const { isOpen: isSubscriptionModalOpen, onOpen: onOpenSubscriptionModal, onClose: onCloseSubscriptionModal } = useDisclosure();
  
  // Add custom site modal state
  const { isOpen: isCustomSiteModalOpen, onOpen: onOpenCustomSiteModal, onClose: onCloseCustomSiteModal } = useDisclosure();
  const [customSiteName, setCustomSiteName] = useState('');
  const [customSiteUrl, setCustomSiteUrl] = useState('');
  const [customSiteIcon, setCustomSiteIcon] = useState('letter');
  const toast = useToast();
  
  // Available default icons for selection
  const availableIcons = [
    'letter', 'default.webp', 'netflix.webp', 'youtube.webp', 'spotify.webp', 
    'prime_video.webp', 'disney_plus.webp', 'hulu.webp', 'twitch.webp',
    'apple_tv_plus.webp', 'hbo_max.webp', 'peacock.webp', 'paramount_plus.webp'
  ];
  
  // Function to validate URL
  const isValidUrl = (urlString: string): boolean => {
    try {
      // If URL doesn't have protocol, add https://
      if (!urlString.match(/^[a-zA-Z]+:\/\//)) {
        urlString = 'https://' + urlString;
      }
      new URL(urlString);
      return true;
    } catch (e) {
      return false;
    }
  };
  
  // Function to handle adding a custom site
  const handleAddCustomSite = () => {
    // Validate input
    if (!customSiteName.trim()) {
      toast({
        title: "Name required",
        description: "Please enter a name for your custom site",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    if (!customSiteUrl.trim() || !isValidUrl(customSiteUrl)) {
      toast({
        title: "Valid URL required",
        description: "Please enter a valid website URL",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    // Ensure URL has proper format
    let formattedUrl = customSiteUrl;
    if (!formattedUrl.match(/^[a-zA-Z]+:\/\//)) {
      formattedUrl = 'https://' + formattedUrl;
    }
    
    // Create new custom site object - using the exact same format as other pins
    const newCustomSite = {
      name: customSiteName,
      url: formattedUrl,
      icon: customSiteIcon
    };
    
    // Add to pinned apps
    setPinnedApps(current => [...current, newCustomSite]);
    
    // Reset form
    setCustomSiteName('');
    setCustomSiteUrl('');
    setCustomSiteIcon('letter');
    
    // Close modal
    onCloseCustomSiteModal();
    
    // Show success toast
    toast({
      title: "Custom site added",
      description: `${customSiteName} has been added to your pinned sites`,
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  // Convert between array and position-based dictionary formats
  const arrayToPinnedDict = (apps: Array<{name: string; icon: string; url: string}>) => {
    return apps.reduce((dict, app, index) => {
      dict[`${index + 1}`] = app;
      return dict;
    }, {} as Record<string, {name: string; icon: string; url: string}>);
  };

  const pinnedDictToArray = (dict: Record<string, {name: string; icon: string; url: string}>) => {
    return Object.entries(dict)
      .sort(([posA], [posB]) => parseInt(posA) - parseInt(posB))
      .map(([_pos, app]) => app);
  };

  // Load pinned apps from Supabase or localStorage
  React.useEffect(() => {
    const loadPinnedApps = async () => {
      setIsLoadingPinnedApps(true);
      try {
        if (user) {
          // Check if user is subscribed before making API request
          if (!isSubscriber) {
            console.log('User is not subscribed, using localStorage only for theatre apps');
            const saved = localStorage.getItem('pinnedApps');
            setPinnedApps(saved ? JSON.parse(saved) : []);
            setIsLoadingPinnedApps(false);
            return;
          }
          
          // User is subscribed, proceed with Supabase request
          console.log('Attempting to load pinned apps from Supabase');
          const { data, error } = await supabase
            .from('user_pinned_apps')
            .select('pinned_apps')
            .eq('user_id', user.id)
            .single();
          
          if (error) {
            if (error.code !== 'PGRST116') { // Not found error
              console.error('Error loading pinned apps from Supabase:', error);
            }
            
            // If not found or error, fall back to localStorage
            const localApps = localStorage.getItem('pinnedApps');
            if (localApps) {
              const parsedApps = JSON.parse(localApps);
              setPinnedApps(parsedApps);
              
              // Save local apps to Supabase for future use using the new format
              await supabase.from('user_pinned_apps').upsert({
                user_id: user.id,
                pinned_apps: {
                  theatre: arrayToPinnedDict(parsedApps),
                  games: {} // Empty games object if this is a new record
                }
              });
              console.log('Saved local pinned apps to Supabase with new format');
            } else {
              setPinnedApps([]);
            }
          } else if (data) {
            console.log('Found pinned apps in Supabase:', data.pinned_apps);
            // Get the theatre section from the nested structure
            const theatreData = data.pinned_apps.theatre || {};
            
            // Convert from position dictionary to array
            const appsArray = pinnedDictToArray(theatreData);
            setPinnedApps(appsArray);
            
            // Update localStorage for offline access
            localStorage.setItem('pinnedApps', JSON.stringify(appsArray));
          }
        } else {
          // Not logged in, use localStorage only
          console.log('User not authenticated, using localStorage only for theatre apps');
          const saved = localStorage.getItem('pinnedApps');
          setPinnedApps(saved ? JSON.parse(saved) : []);
        }
      } catch (error) {
        console.error('Error loading pinned apps:', error);
        // Fall back to localStorage on any error
        const saved = localStorage.getItem('pinnedApps');
        setPinnedApps(saved ? JSON.parse(saved) : []);
      } finally {
        setIsLoadingPinnedApps(false);
      }
    };
    
    loadPinnedApps();
  }, [user, isSubscriber]);

  // Save pinned apps to Supabase and localStorage whenever they change
  React.useEffect(() => {
    const savePinnedApps = async () => {
      // Skip saving during initial load
      if (isLoadingPinnedApps) return;
      
      // Always save to localStorage for offline access
      localStorage.setItem('pinnedApps', JSON.stringify(pinnedApps));
      
      // If user is logged in and subscribed, save to Supabase
      if (user && isSubscriber) {
        try {
          // First, get the current record to preserve games data
          const { data, error: fetchError } = await supabase
            .from('user_pinned_apps')
            .select('pinned_apps')
            .eq('user_id', user.id)
            .single();
          
          // Default empty games object if no existing record
          const gamesData = data?.pinned_apps?.games || {};
          
          // Update with new theatre data but keep games data
          const { error } = await supabase.from('user_pinned_apps').upsert({
            user_id: user.id,
            pinned_apps: {
              theatre: arrayToPinnedDict(pinnedApps),
              games: gamesData
            },
            updated_at: new Date().toISOString()
          });
          
          if (error) {
            console.error('Error saving pinned apps to Supabase:', error);
          } else {
            console.log('Saved pinned apps to Supabase');
          }
        } catch (error) {
          console.error('Failed to save pinned apps to Supabase:', error);
        }
      } else {
        console.log('User not authenticated or not subscribed, saving theatre apps to localStorage only');
      }
    };
    
    savePinnedApps();
  }, [pinnedApps, user, isLoadingPinnedApps, isSubscriber]);

  // Handle clicking outside of apps to exit pin mode
  const handleClickOutside = React.useCallback((e: MouseEvent) => {
    const target = e.target as HTMLElement;
    const isAppButton = target.closest('.app-button');
    const isAddPinButton = target.closest('.add-pin-button');
    const isAddCustomButton = target.closest('.add-custom-site-button');
    const isPinModeToggleButton = target.closest('.pin-mode-toggle-btn');
    
    if (!isAppButton && !isAddPinButton && !isAddCustomButton && !isPinModeToggleButton && isPinMode) {
      // When exiting pin mode, ensure we save the current pinned apps
      if (user && isSubscriber) {
        // Save to Supabase explicitly when exiting pin mode
        const savePinnedAppsOnExit = async () => {
          try {
            // First, get the current record to preserve games data
            const { data, error: fetchError } = await supabase
              .from('user_pinned_apps')
              .select('pinned_apps')
              .eq('user_id', user.id)
              .single();
            
            // Default empty games object if no existing record
            const gamesData = data?.pinned_apps?.games || {};
            
            await supabase.from('user_pinned_apps').upsert({
              user_id: user.id,
              pinned_apps: {
                theatre: arrayToPinnedDict(pinnedApps),
                games: gamesData
              },
              updated_at: new Date().toISOString()
            });
            console.log('Saved pinned apps to Supabase when exiting pin mode');
          } catch (error) {
            console.error('Failed to save pinned apps to Supabase when exiting pin mode:', error);
          }
        };
        
        savePinnedAppsOnExit();
      } else {
        console.log('User not authenticated or not subscribed, saving to localStorage only when exiting pin mode');
      }
      
      // Always save to localStorage
      localStorage.setItem('pinnedApps', JSON.stringify(pinnedApps));
      
      setIsPinMode(false);
    }
  }, [isPinMode, user, pinnedApps, isSubscriber]);

  React.useEffect(() => {
    if (isPinMode) {
      document.addEventListener('click', handleClickOutside);
    } else {
      document.removeEventListener('click', handleClickOutside);
    }
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isPinMode, handleClickOutside]);

  const handlePinToggle = (app: AppItemProps | {name: string; icon: string; url: string}) => {
    // Special case: empty app name means we're toggling pin mode itself
    if (!app.name) {
      // Check subscription status when trying to enable pin mode
      if (!isPinMode && !isSubscriber) {
        // Non-subscribers can't enter pin mode
        onOpenSubscriptionModal();
        return;
      }
      
      // If toggling pin mode OFF, save to Supabase
      if (isPinMode && user && isSubscriber) {
        const savePinnedAppsOnToggle = async () => {
          try {
            // First, get the current record to preserve games data
            const { data, error: fetchError } = await supabase
              .from('user_pinned_apps')
              .select('pinned_apps')
              .eq('user_id', user.id)
              .single();
            
            // Default empty games object if no existing record
            const gamesData = data?.pinned_apps?.games || {};
            
            await supabase.from('user_pinned_apps').upsert({
              user_id: user.id,
              pinned_apps: {
                theatre: arrayToPinnedDict(pinnedApps),
                games: gamesData
              },
              updated_at: new Date().toISOString()
            });
            console.log('Saved pinned apps to Supabase when toggling pin mode');
          } catch (error) {
            console.error('Failed to save pinned apps to Supabase when toggling pin mode:', error);
          }
        };
        
        savePinnedAppsOnToggle();
      } else if (isPinMode) {
        console.log('User not authenticated or not subscribed, saving to localStorage only when toggling pin mode');
        localStorage.setItem('pinnedApps', JSON.stringify(pinnedApps));
      }
      
      setIsPinMode(!isPinMode);
      return;
    }
    
    // Get the standard format for our app object
    const appItem = 'icon' in app 
      ? app 
      : { name: app.name, url: app.url, icon: app.icon as string };
    
    // Toggle the pinned status
    setPinnedApps(current => {
      const isPinned = current.some(pinned => pinned.name === appItem.name);
      if (isPinned) {
        // Remove from pinned apps
        return current.filter(pinned => pinned.name !== appItem.name);
      } else {
        // Add to pinned apps
        return [...current, appItem as {name: string; icon: string; url: string}];
      }
    });
  };

  // Streaming Platforms
  const categories = [
    {
      title: "Streaming Platforms",
      color: "blue",
      apps: [
        {
          name: "Netflix",
          icon: "netflix.webp",
          url: "https://netflix.com"
        },
        {
          name: "YouTube",
          icon: "youtube.webp",
          url: "https://youtube.com"
        },
        {
          name: "Amazon Prime",
          icon: "prime_video.webp",
          url: "https://primevideo.com"
        },
        {
          name: "Disney+",
          icon: "disney_plus.webp",
          url: "https://disneyplus.com"
        },
        {
          name: "Hulu",
          icon: "hulu.webp",
          url: "https://hulu.com"
        },
        {
          name: "Max",
          icon: "hbo_max.webp",
          url: "https://max.com"
        },
        {
          name: "Peacock",
          icon: "peacock.webp",
          url: "https://peacocktv.com"
        },
        {
          name: "Paramount+",
          icon: "paramount_plus.webp",
          url: "https://paramountplus.com"
        },
        {
          name: "Apple TV+",
          icon: "apple_tv_plus.webp",
          url: "https://tv.apple.com"
        },
        {
          name: "Tubi TV",
          icon: "tubi_tv.webp",
          url: "https://tubitv.com"
        },
        {
          name: "Pluto TV",
          icon: "pluto_tv.webp",
          url: "https://pluto.tv"
        },
        {
          name: "Discovery",
          icon: "discovery.webp",
          url: "https://discoveryplus.com"
        },
        {
          name: "Roku Channel",
          icon: "roku_channel.webp",
          url: "https://therokuchannel.roku.com"
        },
        {
          name: "Crackle",
          icon: "crackle.webp",
          url: "https://crackle.com"
        },
        {
          name: "Starz",
          icon: "starz.webp",
          url: "https://starz.com"
        },
        {
          name: "FreeVee",
          icon: "freevee.webp",
          url: "https://amazon.com/freevee"
        },
        {
          name: "Sling TV",
          icon: "sling.webp",
          url: "https://sling.com"
        },
        {
          name: "Xumo Play",
          icon: "xumo_play.webp",
          url: "https://xumo.tv"
        }
      ]
    },
    {
      title: "Video Entertainment",
      color: "red",
      apps: [
        {
          name: "YouTube TV",
          icon: "youtube_tv.webp",
          url: "https://tv.youtube.com"
        },
        {
          name: "Twitch",
          icon: "twitch.webp",
          url: "https://twitch.tv"
        },
        {
          name: "TikTok",
          icon: "tiktok.webp",
          url: "https://tiktok.com"
        },
        {
          name: "Plex",
          icon: "plex.webp",
          url: "https://app.plex.tv"
        },
        {
          name: "Crunchyroll",
          icon: "crunchyroll.webp",
          url: "https://crunchyroll.com"
        },
        {
          name: "Vimeo",
          icon: "vimeo.webp",
          url: "https://vimeo.com"
        }
      ]
    },
    {
      title: "Sports & News",
      color: "orange",
      apps: [
        {
          name: "ESPN",
          icon: "espn.webp",
          url: "https://espn.com"
        },
        {
          name: "CNN",
          icon: "cnn.webp",
          url: "https://cnn.com"
        },
        {
          name: "Fox News",
          icon: "fox_news.webp",
          url: "https://foxnews.com"
        },
        {
          name: "Fox Sports",
          icon: "fox_sports.webp",
          url: "https://foxsports.com"
        },
        {
          name: "NFL",
          icon: "nfl.webp",
          url: "https://nfl.com"
        },
        {
          name: "NBA",
          icon: "nba.webp",
          url: "https://nba.com"
        },
        {
          name: "MLB",
          icon: "mlb.webp",
          url: "https://mlb.com"
        },
        {
          name: "CBS Sports",
          icon: "cbs_sports.webp",
          url: "https://cbssports.com"
        },
        {
          name: "BBC",
          icon: "bbc.webp",
          url: "https://bbc.com"
        },
        {
          name: "Bloomberg",
          icon: "bloomberg.webp",
          url: "https://bloomberg.com"
        }
      ]
    },
    {
      title: "Music Streaming",
      color: "purple",
      apps: [
        {
          name: "Spotify",
          icon: "spotify.webp",
          url: "https://open.spotify.com"
        },
        {
          name: "Apple Music",
          icon: "apple_music.webp",
          url: "https://music.apple.com"
        },
        {
          name: "Amazon Music",
          icon: "amazon_music.webp",
          url: "https://music.amazon.com"
        },
        {
          name: "Pandora",
          icon: "pandora.webp",
          url: "https://pandora.com"
        },
        {
          name: "SoundCloud",
          icon: "soundcloud.webp",
          url: "https://soundcloud.com"
        },
        {
          name: "Tidal",
          icon: "tidal.webp",
          url: "https://tidal.com"
        }
      ]
    },
    {
      title: "Specialty Channels",
      color: "green",
      apps: [
        {
          name: "AMC",
          icon: "amc.webp",
          url: "https://amc.com"
        },
        {
          name: "History",
          icon: "history.webp",
          url: "https://history.com"
        },
        {
          name: "Shudder",
          icon: "shudder.webp",
          url: "https://shudder.com"
        },
        {
          name: "MGM+",
          icon: "mgm_plus.webp",
          url: "https://mgmplus.com"
        },
        {
          name: "Criterion",
          icon: "criterion.webp",
          url: "https://criterionchannel.com"
        },
        {
          name: "Mubi",
          icon: "mubi.webp",
          url: "https://mubi.com"
        }
      ]
    }
  ];
  
  // Add isPinned flag to all apps and update URLs with fullscreen parameter
  const categoriesWithPins = categories.map(category => ({
    ...category,
    apps: category.apps.map(app => ({
      ...app,
      isPinned: pinnedApps.some(pinned => pinned.name === app.name),
      url: app.url
    }))
  }));

  // Update pinned apps URLs
  const pinnedAppsWithFullscreen = pinnedApps.map(app => ({
    ...app,
    url: app.url,
    isPinned: true
  }));

  // Custom center component for the header
  const headerCenterComponent = (
    <Heading 
      fontSize={{ base: 'xl', md: '2xl' }} 
      bgGradient="linear(to-r, blue.400, teal.200)"
      bgClip="text"
      letterSpacing="tight"
    >
      THEATRE
    </Heading>
  );

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState<string | null>(null);

  // Add Supabase auth hook
  const { signOut: supabaseSignOut } = useAuth();

  // Add navigation hook
  const navigate = useNavigate();

  useEffect(() => {
    // Check for existing session using cached auth call
    const checkSession = async () => {
      const userData = await getCachedAuthUser();
      if (userData) {
        setIsAuthenticated(true);
        setUsername(userData.email || null);
      }
    };
    
    checkSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session);
      setUsername(session?.user?.email || null);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Update the handleLogout function
  const handleLogout = async () => {
    try {
      await supabaseSignOut();
      navigate('/');
      console.log('Logged out successfully from TheatreApps');
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  // Function to handle pin mode toggle with subscription check
  const handlePinModeToggle = (e?: React.MouseEvent) => {
    // If event is provided, stop propagation
    if (e) {
      e.stopPropagation();
    }
    
    if (isSubscriber) {
      setIsPinMode(!isPinMode);
    } else {
      // Open subscription required modal
      onOpenSubscriptionModal();
    }
  };

  // Handle button click based on state
  const handleButtonClick = () => {
    if (isPinMode) {
      // Exiting pin mode
      handlePinModeToggle();
    } else {
      // Toggle fullscreen
      setIsFullscreen(!isFullscreen);
    }
  };

  // Add this helper function to get a background color for the letter icon
  const getLetterBgColor = (siteName: string) => {
    const colors = [
      "blue.500", "purple.500", "green.500", "red.500", "orange.500", 
      "teal.500", "cyan.500", "pink.500", "yellow.500"
    ];
    
    // Create a simple hash from the site name
    let hash = 0;
    for (let i = 0; i < siteName.length; i++) {
      hash = siteName.charCodeAt(i) + ((hash << 5) - hash);
    }
    hash = Math.abs(hash);
    
    // Use the hash to pick a color
    return colors[hash % colors.length];
  };

  return (
    <Box 
      bg="#000" 
      color={textColor} 
      minHeight="100vh" 
      height="100vh"
      overflow="hidden"
      bgGradient={bgGradient}
      position="relative"
      _before={{
        content: '""',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundImage: "url('https://images.unsplash.com/photo-1483000805330-4eaf0a0d82da?q=80&w=2070')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        opacity: 0.08,
        zIndex: 0
      }}
    >
      {/* Replace the custom header with AppHeader */}
      <AppHeader
        playbackState={null}
        layoutMode="split"
        setLayoutMode={() => {}}
        accentColor="#1DB954"
        pageTitle="Theatre Apps"
        isCondensed={true}
      />

      {/* Main Content */}
      <Box 
        position="relative" 
        zIndex="1"
        pt={isFullscreen ? 0 : 20}
        px={isFullscreen ? 0 : 6}
        height={isFullscreen ? "100vh" : "calc(100vh - 72px)"}
        overflow="auto"
        css={{
          '&::-webkit-scrollbar': {
            display: 'none'
          },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none'
        }}
      >
        {!isFullscreen && (
          <Flex justify="space-between" align="center" mb={6}>
            <Heading 
              as="h1" 
              size="xl"
              bgGradient="linear(to-r, blue.400, purple.300)"
              bgClip="text"
              letterSpacing="tight"
            >
              Entertainment Hub
            </Heading>
            
            <HStack>
              <Tooltip>
                <Button
                  leftIcon={<FaPlus />}
                  onClick={() => {
                    // Check if user is subscribed
                    if (isSubscriber) {
                      onOpenCustomSiteModal();
                    } else {
                      // Show subscription modal for non-subscribers
                      onOpenSubscriptionModal();
                    }
                  }}
                >
                  Add Custom Site
                </Button>
              </Tooltip>
              <Tooltip>
                <Button
                  leftIcon={<FaThumbtack />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePinModeToggle();
                  }}
                  bg={isPinMode ? "green.500" : "blue.500"}
                  _hover={{ bg: isPinMode ? "green.600" : "blue.600" }}
                  color="white"
                  fontSize="sm"
                  px={4}
                  py={2}
                  borderRadius="md"
                  shadow="md"
                  className="pin-mode-toggle-btn"
                >
                  {isPinMode ? "Exit Pin Mode" : "Enter Pin Mode"}
                </Button>
              </Tooltip>
            </HStack>
          </Flex>
        )}
        
        {/* Pinned Apps Section */}
        <Box mb={8}>
          <Heading 
            size="md" 
            mb={4}
            pl={4}
            color="white"
            fontWeight="semibold"
            letterSpacing="wider"
            textAlign="left"
          >
            {isPinMode ? "Select Your Favorites" : "Your Apps"}
          </Heading>
          
          <Box
            position="relative"
            p={1}
          >
            {isLoadingPinnedApps ? (
              <Flex justify="center" align="center" height="200px">
                <Spinner 
                  color="blue.400" 
                  size="xl" 
                  thickness="4px" 
                  speed="0.65s"
                />
              </Flex>
            ) : (
              pinnedApps.length === 0 ? (
                <Box 
                  py={8} 
                  textAlign="center" 
                  width="100%" 
                  borderRadius="lg" 
                  borderWidth="1px" 
                  borderColor="whiteAlpha.100"
                  bg="rgba(0, 0, 0, 0.3)"
                >
                  <Button
                    leftIcon={<FaThumbtack />}
                    onClick={(e) => handlePinModeToggle(e)}
                    variant="outline"
                    size="lg"
                    colorScheme="blue"
                    mb={4}
                    className="pin-mode-toggle-btn"
                  >
                    Pin Your Favorite Apps
                  </Button>
                  <Text color="gray.400">
                    You haven't pinned any apps yet. Click the button above to enter pin mode.
                  </Text>
                </Box>
              ) : (
                <Flex flexWrap="wrap" gap={4} px={4}>
                  {pinnedApps.map((app, index) => (
                    <Box key={`${app.name}-${index}`}>
                      <AppButton 
                        item={{
                          id: app.name,
                          name: app.name,
                          url: app.url,
                          color: "blue",
                          isPinned: true,
                          isPinMode: isPinMode,
                          isFullscreen: isFullscreen
                        }}
                        type="theatre"
                        iconName={app.icon}
                        onPinToggle={handlePinToggle}
                      />
                    </Box>
                  ))}
                </Flex>
              )
            )}
          </Box>
        </Box>

        {/* App Categories */}
        {categoriesWithPins.map(category => (
          <CategoryRow 
            key={category.title} 
            title={category.title} 
            apps={category.apps} 
            color={category.color}
            onPinToggle={handlePinToggle}
            isPinMode={isPinMode}
            isFullscreen={isFullscreen}
          />
        ))}
      </Box>

      {/* Modals */}
      {/* Custom Site Modal */}
      <Modal isOpen={isCustomSiteModalOpen} onClose={onCloseCustomSiteModal}>
        <ModalOverlay backdropFilter="blur(10px)" />
        <ModalContent bg="rgba(0, 0, 0, 0.8)" color="white" borderRadius="xl" borderWidth="1px" borderColor="whiteAlpha.200">
          <ModalHeader>Add Custom Site</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Site Name</FormLabel>
                <Input 
                  placeholder="My Custom Site" 
                  value={customSiteName}
                  onChange={(e) => setCustomSiteName(e.target.value)}
                  bg="whiteAlpha.100"
                  borderColor="whiteAlpha.300"
                  _hover={{ borderColor: "whiteAlpha.400" }}
                />
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel>URL</FormLabel>
                <Input 
                  placeholder="https://example.com" 
                  value={customSiteUrl}
                  onChange={(e) => setCustomSiteUrl(e.target.value)}
                  bg="whiteAlpha.100"
                  borderColor="whiteAlpha.300"
                  _hover={{ borderColor: "whiteAlpha.400" }}
                />
                <FormHelperText color="whiteAlpha.600">Enter the website URL (https:// will be added if missing)</FormHelperText>
              </FormControl>
              
              <FormControl>
                <FormLabel>Choose Icon</FormLabel>
                <Grid templateColumns="repeat(4, 1fr)" gap={3} mt={2}>
                  <Box 
                    key="letter-icon"
                    onClick={() => setCustomSiteIcon('letter')}
                    border={customSiteIcon === 'letter' ? "2px solid" : "2px solid transparent"}
                    borderColor={customSiteIcon === 'letter' ? "blue.400" : "transparent"}
                    borderRadius="md"
                    p={2}
                    cursor="pointer"
                    _hover={{ bg: "whiteAlpha.100" }}
                  >
                    <Flex
                      boxSize="40px"
                      align="center"
                      justify="center"
                      borderRadius="md"
                      bg={customSiteName ? getLetterBgColor(customSiteName) : "blue.500"}
                      mx="auto"
                    >
                      <Text
                        fontSize="24px"
                        fontWeight="bold"
                        color="white"
                      >
                        {customSiteName ? customSiteName.charAt(0).toUpperCase() : 'A'}
                      </Text>
                    </Flex>
                  </Box>
                  
                  {availableIcons
                    .filter(icon => icon !== 'letter')
                    .map(icon => (
                      <Box 
                        key={icon}
                        onClick={() => setCustomSiteIcon(icon)}
                        border={customSiteIcon === icon ? "2px solid" : "2px solid transparent"}
                        borderColor={customSiteIcon === icon ? "blue.400" : "transparent"}
                        borderRadius="md"
                        p={2}
                        cursor="pointer"
                        _hover={{ bg: "whiteAlpha.100" }}
                      >
                        <Image 
                          src={importAppIcon(icon)}
                          boxSize="40px"
                          objectFit="contain"
                          mx="auto"
                          borderRadius="md"
                        />
                      </Box>
                    ))
                  }
                </Grid>
              </FormControl>
            </VStack>
          </ModalBody>
          
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onCloseCustomSiteModal}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleAddCustomSite}>
              Add Site
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Existing Subscription Modal */}
      <Modal isOpen={isSubscriptionModalOpen} onClose={onCloseSubscriptionModal}>
        <ModalOverlay backdropFilter="blur(10px)" />
        <ModalContent bg="rgba(0, 0, 0, 0.8)" color="white" borderRadius="xl" borderWidth="1px" borderColor="whiteAlpha.200">
          <ModalHeader>Premium Feature</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              Pinning apps and adding custom sites are premium features. Please subscribe to access this functionality.
            </Text>
          </ModalBody>
          
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={onCloseSubscriptionModal}>
              Close
            </Button>
            <Button variant="outline" as={RouterLink} to="/subscription">
              View Plans
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default TheatreApps; 